// src/pages/CampaignsCommercialPage.jsx
// ORBIS PMO — C1.6 Campañas Comerciales CRM
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { API_BASE } from "../config/orbisConfig";


const PRODUCT_LINES = [
    "",
    "ORBIS PMO",
    "Gestión institucional",
    "CRM institucional",
    "Proyectos y compromisos",
    "Eventos institucionales",
    "Directorio estratégico",
    "Otro"
];
async function safeJson(url) {
    const res = await fetch(url);
    const text = await res.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; }
    catch { throw new Error(`Respuesta no JSON (${res.status}): ${text.slice(0, 220)}`); }
    if (!res.ok || data?.success === false) throw new Error(data?.error || data?.detail || `HTTP ${res.status}`);
    return data;
}

function n(value) { const x = Number(value || 0); return Number.isFinite(x) ? x : 0; }
function pct(value) { return `${Math.round(n(value))}%`; }
function money(value, currency = "MXN") { return `${currency || "MXN"} ${n(value).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`; }

function Badge({ children, tone = "gray" }) {
    const tones = {
        gray: "bg-gray-50 text-gray-700 border-gray-200",
        blue: "bg-blue-50 text-blue-700 border-blue-200",
        green: "bg-green-50 text-green-700 border-green-200",
        amber: "bg-amber-50 text-amber-700 border-amber-200",
        red: "bg-red-50 text-red-700 border-red-200",
        purple: "bg-purple-50 text-purple-700 border-purple-200",
    };
    return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${tones[tone] || tones.gray}`}>{children}</span>;
}

function Kpi({ title, value, subtitle, tone = "default" }) {
    const tones = {
        default: "bg-white border-cani-border",
        blue: "bg-blue-50 border-blue-200",
        green: "bg-green-50 border-green-200",
        amber: "bg-amber-50 border-amber-200",
        red: "bg-red-50 border-red-200",
        purple: "bg-purple-50 border-purple-200",
    };
    return (
        <div className={`rounded-2xl border shadow-sm p-4 ${tones[tone] || tones.default}`}>
            <div className="text-[11px] text-gray-500">{title}</div>
            <div className="text-2xl font-semibold text-cani-dark mt-1">{value}</div>
            {subtitle ? <div className="text-[10px] text-gray-500 mt-1">{subtitle}</div> : null}
        </div>
    );
}

function ProgressBar({ value, tone = "blue" }) {
    const colors = { red: "#dc2626", amber: "#d97706", blue: "#2563eb", green: "#059669", purple: "#7c3aed" };
    const width = Math.max(0, Math.min(100, n(value)));
    return <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${width}%`, background: colors[tone] || colors.blue }} /></div>;
}

function statusTone(status) {
    if (status === "activa") return "green";
    if (status === "pausada") return "amber";
    if (status === "cerrada") return "gray";
    return "blue";
}

function Style() {
    return <style>{`
    .campaigns-crm table tbody tr { transition: background .18s ease; }
    .campaigns-crm table tbody tr:nth-child(odd) { background: #ffffff; }
    .campaigns-crm table tbody tr:nth-child(even) { background: #f8fafc; }
    .campaigns-crm table tbody tr:hover { background: #eff6ff !important; }
    .campaigns-crm .input-pro { width: 100%; border: 1px solid #e5e7eb; border-radius: .6rem; padding: .45rem .55rem; font-size: 12px; outline: none; background: white; }
    .campaigns-crm .label-pro { display:block; font-size:11px; color:#4b5563; margin-bottom:4px; }
  `}</style>;
}

export default function CampaignsCommercialPage() {
    const navigate = useNavigate();

    const [summary, setSummary] = useState({});
    const [campaigns, setCampaigns] = useState([]);
    const [filters, setFilters] = useState({ status: "", product_line: "" });
    const [sort, setSort] = useState({ key: "opportunities", direction: "desc" });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    function buildQuery(extra = {}) {
        const merged = { ...filters, ...extra };
        const params = new URLSearchParams();
        Object.entries(merged).forEach(([k, v]) => { if (v !== "") params.set(k, v); });
        return params.toString();
    }

    async function load(extra = {}) {
        try {
            setLoading(true);
            setError("");
            const data = await safeJson(`${API_BASE}/campaigns_commercial_dashboard.php?${buildQuery(extra)}`);
            setSummary(data.summary || {});
            setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
        } catch (err) {
            console.error(err);
            setError(err.message || "No se pudieron cargar campañas comerciales.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    function handleFilterChange(e) {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    }

    function requestSort(key) {
        setSort((prev) => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));
    }

    function SortBtn({ sortKey, children }) {
        const active = sort.key === sortKey;
        return <button type="button" onClick={() => requestSort(sortKey)} className={`inline-flex gap-1 hover:text-cani-primary ${active ? "text-cani-primary" : ""}`}>{children}<span>{active ? (sort.direction === "asc" ? "▲" : "▼") : "↕"}</span></button>;
    }

    const rows = useMemo(() => {
        const dir = sort.direction === "asc" ? 1 : -1;
        return [...campaigns].sort((a, b) => {
            let av = "", bv = "";
            if (sort.key === "opportunities") { av = n(a.opportunities_count); bv = n(b.opportunities_count); }
            else if (sort.key === "pilot") { av = n(a.pilot_value); bv = n(b.pilot_value); }
            else if (sort.key === "monthly") { av = n(a.monthly_potential); bv = n(b.monthly_potential); }
            else if (sort.key === "conversion") { av = n(a.conversion_rate); bv = n(b.conversion_rate); }
            else if (sort.key === "quality") { av = n(a.next_action_rate) + n(a.value_capture_rate) + n(a.contact_rate) + n(a.activity_rate); bv = n(b.next_action_rate) + n(b.value_capture_rate) + n(b.contact_rate) + n(b.activity_rate); }
            else { av = a.name || ""; bv = b.name || ""; }

            if (typeof av === "number" || typeof bv === "number") return (n(av) - n(bv)) * dir;
            return String(av).localeCompare(String(bv), "es", { sensitivity: "base", numeric: true }) * dir;
        });
    }, [campaigns, sort]);

    const executiveTone = n(summary.total_opportunities) === 0 ? "amber" : n(summary.next_action_rate) < 50 ? "red" : "green";

    return (
        <MainLayout>
            <Style />
            <div className="p-4 space-y-4 campaigns-crm">
                <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
                    <div>
                        <h2 className="text-lg md:text-xl font-semibold text-cani-dark">Campañas Comerciales CRM</h2>
                        <p className="text-xs md:text-sm text-gray-500 max-w-3xl">Control de campañas, generación de oportunidades, rendimiento del pipeline y calidad de seguimiento.</p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                        <button type="button" onClick={() => navigate("/crm-dashboard")} className="px-3 py-1.5 rounded-lg border border-cani-border text-cani-dark hover:bg-cani-bg">Dashboard CRM</button>
                        <button type="button" onClick={() => navigate("/opportunities")} className="px-3 py-1.5 rounded-lg border border-cani-border text-cani-dark hover:bg-cani-bg">Oportunidades</button>
                        <button type="button" onClick={() => navigate("/agenda")} className="px-3 py-1.5 rounded-lg border border-cani-border text-cani-dark hover:bg-cani-bg">Agenda</button>
                        <button type="button" onClick={() => load()} className="px-3 py-1.5 rounded-lg bg-cani-primary text-white hover:bg-cani-primaryLight">Recargar</button>
                    </div>
                </div>

                {error ? <div className="text-xs rounded-lg px-3 py-2 border bg-red-50 text-red-700 border-red-200">{error}</div> : null}

                <div className={`rounded-2xl border shadow-sm p-4 ${executiveTone === "red" ? "bg-red-50 border-red-200" : executiveTone === "amber" ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}>
                    <div className="text-[11px] text-gray-500">Lectura ejecutiva de campañas</div>
                    <div className="text-sm font-semibold text-cani-dark mt-1">
                        {n(summary.total_opportunities) === 0 ? "Hay campañas configuradas, pero falta generación de oportunidades." : n(summary.next_action_rate) < 50 ? "Las campañas generan oportunidades, pero necesitan disciplina de seguimiento." : "Las campañas tienen seguimiento comercial activo."}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Evalúa cada campaña por oportunidades, próxima acción, captura de valor, contacto y actividad vinculada.</p>
                </div>

                <div className="bg-white border border-cani-border rounded-2xl shadow-sm p-3">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                        <div>
                            <label className="label-pro">Estatus</label>
                            <select name="status" value={filters.status} onChange={handleFilterChange} className="input-pro">
                                <option value="">Todos</option>
                                <option value="activa">Activa</option>
                                <option value="pausada">Pausada</option>
                                <option value="cerrada">Cerrada</option>
                            </select>
                        </div>
                        <div>
                            <label className="label-pro">Producto / línea</label>
                            <select name="product_line" value={filters.product_line} onChange={handleFilterChange} className="input-pro">
                                {PRODUCT_LINES.map((p) => <option key={p || "all"} value={p}>{p || "Todos"}</option>)}
                            </select>
                        </div>
                        <div className="md:col-span-2 flex items-end justify-end gap-2">
                            <button type="button" onClick={() => { const reset = { status: "", product_line: "" }; setFilters(reset); load(reset); }} className="px-3 py-1.5 rounded-lg border border-cani-border text-gray-700 hover:bg-cani-bg">Limpiar</button>
                            <button type="button" onClick={() => load()} className="px-3 py-1.5 rounded-lg bg-cani-primary text-white hover:bg-cani-primaryLight">Aplicar</button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                    <Kpi title="Campañas" value={summary.total_campaigns || 0} subtitle={`${summary.active_campaigns || 0} activas`} tone="blue" />
                    <Kpi title="Oportunidades" value={summary.total_opportunities || 0} subtitle={`${summary.open_opportunities || 0} abiertas`} tone="amber" />
                    <Kpi title="Conversión" value={pct(summary.conversion_rate)} subtitle={`${summary.won_opportunities || 0} ganadas`} tone="green" />
                    <Kpi title="Próxima acción" value={pct(summary.next_action_rate)} subtitle={`${summary.with_next_action || 0}/${summary.total_opportunities || 0}`} tone={n(summary.next_action_rate) >= 70 ? "green" : "red"} />
                    <Kpi title="Valor piloto" value={money(summary.pilot_value)} subtitle="Pipeline generado" tone="purple" />
                    <Kpi title="Potencial mensual" value={money(summary.monthly_potential)} subtitle="Continuidad estimada" tone="green" />
                    <Kpi title="Captura de valor" value={pct(summary.value_capture_rate)} subtitle={`${summary.with_pilot_value || 0}/${summary.total_opportunities || 0}`} tone={n(summary.value_capture_rate) >= 70 ? "green" : "amber"} />
                    <Kpi title="Con contacto" value={pct(summary.contact_rate)} subtitle={`${summary.with_contact || 0}/${summary.total_opportunities || 0}`} tone={n(summary.contact_rate) >= 70 ? "green" : "amber"} />
                    <Kpi title="Con actividad" value={pct(summary.activity_rate)} subtitle={`${summary.with_activity || 0}/${summary.total_opportunities || 0}`} tone={n(summary.activity_rate) >= 70 ? "green" : "amber"} />
                    <Kpi title="Presupuesto" value={money(summary.budget_total)} subtitle={summary.roi_estimated === null ? "ROI pendiente" : `ROI est. ${pct(summary.roi_estimated)}`} tone="blue" />
                </div>

                {loading ? <div className="bg-white border border-cani-border rounded-2xl p-4 text-sm text-gray-500">Cargando campañas comerciales.</div> : null}

                <div className="bg-white border border-cani-border rounded-2xl shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-cani-border">
                        <h3 className="text-xs font-semibold text-cani-dark">Rendimiento por campaña</h3>
                        <p className="text-[10px] text-gray-500 mt-0.5">Comparativo comercial de generación, calidad de seguimiento y valor potencial.</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-[1500px] w-full text-xs">
                            <thead className="bg-cani-bg">
                                <tr className="text-left text-[11px] text-gray-600 font-semibold">
                                    <th className="px-3 py-2"><SortBtn sortKey="name">Campaña</SortBtn></th>
                                    <th className="px-3 py-2">Segmento / canal</th>
                                    <th className="px-3 py-2"><SortBtn sortKey="opportunities">Oportunidades</SortBtn></th>
                                    <th className="px-3 py-2"><SortBtn sortKey="quality">Calidad</SortBtn></th>
                                    <th className="px-3 py-2"><SortBtn sortKey="pilot">Valor piloto</SortBtn></th>
                                    <th className="px-3 py-2"><SortBtn sortKey="monthly">Mensual</SortBtn></th>
                                    <th className="px-3 py-2"><SortBtn sortKey="conversion">Conversión</SortBtn></th>
                                    <th className="px-3 py-2 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">Sin campañas comerciales.</td></tr>
                                ) : rows.map((c) => (
                                    <tr key={c.id} className="border-t border-cani-border">
                                        <td className="px-3 py-2">
                                            <button type="button" onClick={() => navigate(`/campaigns-commercial/${c.id}`)} className="font-semibold text-cani-dark hover:text-cani-primary text-left">{c.name}</button>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                <Badge tone={statusTone(c.status)}>{c.status || "sin estatus"}</Badge>
                                                {c.landing_url ? <Badge tone="blue">Landing</Badge> : null}
                                                {n(c.budget) > 0 ? <Badge tone="purple">Presupuesto</Badge> : null}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div>{c.segment || "Sin segmento"}</div>
                                            <div className="text-[11px] text-gray-500">{c.channel || "Sin canal"}</div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="font-semibold">{c.opportunities_count || 0}</div>
                                            <div className="text-[11px] text-gray-500">Abiertas {c.open_count || 0} · Ganadas {c.won_count || 0}</div>
                                        </td>
                                        <td className="px-3 py-2 w-64">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div><div className="text-[10px] text-gray-500">Próx. acción {pct(c.next_action_rate)}</div><ProgressBar value={c.next_action_rate} tone={n(c.next_action_rate) >= 70 ? "green" : "red"} /></div>
                                                <div><div className="text-[10px] text-gray-500">Valor {pct(c.value_capture_rate)}</div><ProgressBar value={c.value_capture_rate} tone={n(c.value_capture_rate) >= 70 ? "green" : "amber"} /></div>
                                                <div><div className="text-[10px] text-gray-500">Contacto {pct(c.contact_rate)}</div><ProgressBar value={c.contact_rate} tone={n(c.contact_rate) >= 70 ? "green" : "amber"} /></div>
                                                <div><div className="text-[10px] text-gray-500">Actividad {pct(c.activity_rate)}</div><ProgressBar value={c.activity_rate} tone={n(c.activity_rate) >= 70 ? "green" : "amber"} /></div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">{money(c.pilot_value, c.currency)}</td>
                                        <td className="px-3 py-2">{money(c.monthly_potential, c.currency)}</td>
                                        <td className="px-3 py-2">
                                            <div>{pct(c.conversion_rate)}</div>
                                            <div className="text-[11px] text-gray-500">Prob. {pct(c.avg_probability)}</div>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="flex justify-end gap-1">
                                                <button type="button" onClick={() => navigate(`/campaigns-commercial/${c.id}`)} className="px-2 py-1 rounded-lg border border-cani-border text-gray-700 hover:bg-cani-bg text-[11px]">Ver</button>
                                                <button type="button" onClick={() => navigate(`/opportunities?campaign_id=${c.id}`)} className="px-2 py-1 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-[11px]">Opps</button>
                                                <button type="button" onClick={() => navigate(`/agenda?campaign_id=${c.id}`)} className="px-2 py-1 rounded-lg border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 text-[11px]">Agenda</button>
                                                {c.landing_url ? <a href={c.landing_url} target="_blank" rel="noreferrer" className="px-2 py-1 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 text-[11px]">Landing</a> : null}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
}
